import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError, api, setCsrfToken } from '../api/client';
import type { PrincipalType, SessionData, StaffRole } from '../api/types';

interface AuthContextValue {
  session: SessionData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  acceptSession: (session: SessionData) => void;
  signOut: () => Promise<void>;
  hasRole: (role: StaffRole) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const acceptSession = useCallback((next: SessionData) => {
    setCsrfToken(next.csrfToken);
    setSession(next);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      acceptSession(await api.session());
    } catch (caught) {
      if (caught instanceof ApiError && [401, 403].includes(caught.status)) {
        setCsrfToken();
        setSession(null);
      } else {
        setError(caught instanceof Error ? caught.message : 'Connexion au serveur impossible.');
      }
    } finally {
      setLoading(false);
    }
  }, [acceptSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setCsrfToken();
      setSession(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      error,
      refresh,
      acceptSession,
      signOut,
      hasRole: (role) => Boolean(session?.principal.roles.includes(role)),
    }),
    [acceptSession, error, loading, refresh, session, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// The provider and its colocated hook intentionally share one module.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth doit être utilisé dans AuthProvider.');
  return value;
}

export function RequireAuth({
  children,
  principalType,
  role,
}: PropsWithChildren<{ principalType: PrincipalType; role?: StaffRole }>) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return (
      <main id="contenu" className="route-state" aria-busy="true">
        <span className="spinner" aria-hidden="true" />
        <p>Vérification de votre session…</p>
      </main>
    );
  }

  if (auth.error && !auth.session) {
    return (
      <main id="contenu" className="route-state">
        <div className="state-icon state-icon--error" aria-hidden="true">
          !
        </div>
        <h1>Connexion au service impossible</h1>
        <p>{auth.error}</p>
        <button
          className="button button--primary"
          type="button"
          onClick={() => void auth.refresh()}
        >
          Réessayer
        </button>
      </main>
    );
  }

  const principal = auth.session?.principal;
  if (!principal || principal.type !== principalType) {
    const loginPath =
      principalType === 'guest'
        ? '/connexion'
        : role === 'agent'
          ? '/accueil/connexion'
          : '/admin/connexion';
    return <Navigate replace to={loginPath} state={{ from: location.pathname }} />;
  }

  if (role && !principal.roles.includes(role)) {
    const fallback = principal.roles.includes('agent') ? '/accueil/ceremonies' : '/';
    return <Navigate replace to={fallback} state={{ denied: true }} />;
  }

  return children;
}
